import { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import BONavigation from "../../../components/admin/BONavigation";
import LinkedinFunnelPanel from "./LinkedinFunnelPanel";
import {
    getEnrichmentStatusStats,
    EnrichmentStatusStats,
} from "../../../interfaces/events";

function DFSKpiCard({
    label,
    value,
    total,
    valueColor,
}: {
    label: string;
    value: number;
    total: number;
    valueColor: string;
}) {
    const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${valueColor}`}>
                {value.toLocaleString("fr-FR")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{pct}% du total</p>
        </div>
    );
}

function StatCard({
    label,
    value,
    sub,
    valueColor,
}: {
    label: string;
    value: number | string;
    sub?: string;
    valueColor?: string;
}) {
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${valueColor ?? "text-gray-900"}`}>
                {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
            </p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

type Tab = "done" | "pending" | "google_place_dfs" | "email" | "ai_confidence" | "linkedin";

export default function EnrichmentStatus() {
    const [stats, setStats] = useState<EnrichmentStatusStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<Tab>("done");

    useEffect(() => {
        getEnrichmentStatusStats()
            .then(setStats)
            .finally(() => setLoading(false));
    }, []);

    const labels = stats?.weeklyData.map((w) => w.label) ?? [];
    const googlePlaceDoneSeries = stats?.weeklyData.map((w) => w.googlePlaceDone) ?? [];
    const googlePlacePendingSeries = stats?.weeklyData.map((w) => w.googlePlacePending) ?? [];
    const googlePlaceDFSDoneSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSDone) ?? [];
    const googlePlaceDFSPendingSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSPending) ?? [];
    const linkedinPendingSeries = stats?.weeklyData.map((w) => w.linkedinPending) ?? [];
    const foundersAddedSeries = stats?.weeklyData.map((w) => w.foundersAdded) ?? [];
    const birthdateCompletedSeries = stats?.weeklyData.map((w) => w.birthdateCompleted) ?? [];
    const birthdatePendingSeries = stats?.weeklyData.map((w) => w.birthdatePending) ?? [];
    const noPeopleSeries = stats?.weeklyData.map((w) => w.noPeopleEvents) ?? [];
    const noWebsiteSeries = stats?.weeklyData.map((w) => w.noWebsiteEvents) ?? [];
    const noEmailSeries = stats?.weeklyData.map((w) => w.noEmailEvents) ?? [];
    const dfsPendingSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusPending) ?? [];
    const dfsDoneSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusDone) ?? [];
    const dfsWaitingCallbackSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusWaitingCallback) ?? [];
    const dfsApprovedSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusApproved) ?? [];
    const dfsErrorSeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusError) ?? [];
    const dfsRetrySeries = stats?.weeklyData.map((w) => w.googlePlaceDFSStatusRetry) ?? [];

    const emailDoneSeries = stats?.weeklyData.map((w) => w.emailDone) ?? [];
    const emailPendingNoWebsiteSeries = stats?.weeklyData.map((w) => w.emailPendingNoWebsite) ?? [];
    const emailPendingWithWebsiteSeries = stats?.weeklyData.map((w) => w.emailPendingWithWebsite) ?? [];

    const monthlyLabels = stats?.monthlyDFSData.map((m) => m.label) ?? [];
    const monthlyDfsPendingSeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusPending) ?? [];
    const monthlyDfsDoneSeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusDone) ?? [];
    const monthlyDfsWaitingCallbackSeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusWaitingCallback) ?? [];
    const monthlyDfsApprovedSeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusApproved) ?? [];
    const monthlyDfsErrorSeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusError) ?? [];
    const monthlyDfsRetrySeries = stats?.monthlyDFSData.map((m) => m.googlePlaceDFSStatusRetry) ?? [];

    const monthlyEmailDoneSeries = stats?.monthlyDFSData.map((m) => m.emailDone) ?? [];
    const monthlyEmailPendingNoWebsiteSeries = stats?.monthlyDFSData.map((m) => m.emailPendingNoWebsite) ?? [];
    const monthlyEmailPendingWithWebsiteSeries = stats?.monthlyDFSData.map((m) => m.emailPendingWithWebsite) ?? [];

    const confidenceHighSeries = stats?.weeklyData.map((w) => w.aiConfidenceLevelHigh) ?? [];
    const confidenceMediumSeries = stats?.weeklyData.map((w) => w.aiConfidenceLevelMedium) ?? [];
    const confidenceLowSeries = stats?.weeklyData.map((w) => w.aiConfidenceLevelLow) ?? [];
    const confidenceUnverifiedSeries = stats?.weeklyData.map((w) => w.aiConfidenceLevelUnverified) ?? [];

    const monthlyConfidenceHighSeries = stats?.monthlyDFSData.map((m) => m.aiConfidenceLevelHigh) ?? [];
    const monthlyConfidenceMediumSeries = stats?.monthlyDFSData.map((m) => m.aiConfidenceLevelMedium) ?? [];
    const monthlyConfidenceLowSeries = stats?.monthlyDFSData.map((m) => m.aiConfidenceLevelLow) ?? [];
    const monthlyConfidenceUnverifiedSeries = stats?.monthlyDFSData.map((m) => m.aiConfidenceLevelUnverified) ?? [];

    const barOptions = (color: string): ApexCharts.ApexOptions => ({
        chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "inherit",
            background: "transparent",
        },
        plotOptions: {
            bar: { borderRadius: 3, columnWidth: "55%" },
        },
        colors: [color],
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels,
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
        },
        grid: {
            borderColor: "#f3f4f6",
            strokeDashArray: 3,
            xaxis: { lines: { show: false } },
        },
        tooltip: {
            theme: "light",
            y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "left",
            markers: { width: 8, height: 8 },
            labels: { colors: "#374151" },
            fontSize: "12px",
        },
    });

    // Calculate totals for current week
    const currentWeekData = stats?.weeklyData[stats.weeklyData.length - 1];
    const totalGooglePlaceDone = currentWeekData?.googlePlaceDone ?? 0;
    const totalGooglePlacePending = currentWeekData?.googlePlacePending ?? 0;
    const totalGooglePlaceDFSDone = currentWeekData?.googlePlaceDFSDone ?? 0;
    const totalGooglePlaceDFSPending = currentWeekData?.googlePlaceDFSPending ?? 0;
    const totalLinkedinPending = currentWeekData?.linkedinPending ?? 0;
    const totalFoundersAdded = currentWeekData?.foundersAdded ?? 0;
    const totalBirthdateCompleted = currentWeekData?.birthdateCompleted ?? 0;
    const totalBirthdatePending = currentWeekData?.birthdatePending ?? 0;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                <h1 className="text-xl font-semibold text-gray-900 mb-6">
                    Statut des enrichissements
                </h1>

                {loading ? (
                    <div className="text-sm text-gray-400">Chargement…</div>
                ) : !stats ? (
                    <div className="text-sm text-red-500">Erreur lors du chargement.</div>
                ) : (
                    <>
                        {/* Summary Stats Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <StatCard
                                label="Google Place récupérés"
                                value={totalGooglePlaceDone}
                                sub="cette semaine"
                                valueColor="text-amber-600"
                            />
                            <StatCard
                                label="Google Place en attente"
                                value={totalGooglePlacePending}
                                sub="cette semaine"
                                valueColor="text-orange-600"
                            />
                            <StatCard
                                label="Google Place DFS récupérés"
                                value={totalGooglePlaceDFSDone}
                                sub="cette semaine"
                                valueColor="text-yellow-600"
                            />
                            <StatCard
                                label="Google Place DFS en attente"
                                value={totalGooglePlaceDFSPending}
                                sub="cette semaine"
                                valueColor="text-red-600"
                            />
                            <StatCard
                                label="LinkedIn en attente"
                                value={totalLinkedinPending}
                                sub="cette semaine"
                                valueColor="text-purple-600"
                            />
                            <StatCard
                                label="Fondateurs ajoutés"
                                value={totalFoundersAdded}
                                sub="cette semaine"
                                valueColor="text-gray-700"
                            />
                            <StatCard
                                label="Dates de naissance"
                                value={totalBirthdateCompleted}
                                sub="cette semaine"
                                valueColor="text-green-600"
                            />
                            <StatCard
                                label="Dates de naissance en attente"
                                value={totalBirthdatePending}
                                sub="cette semaine"
                                valueColor="text-teal-600"
                            />
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 mb-4 border-b border-gray-200">
                            {([
                                { key: "done", label: "Réalisés" },
                                { key: "pending", label: "En attente / Bloqués" },
                                { key: "google_place_dfs", label: "Google Place DFS" },
                                { key: "email", label: "Email" },
                                { key: "linkedin", label: "LinkedIn" },
                                { key: "ai_confidence", label: "AI Confidence Maps" },
                            ] as { key: Tab; label: string }[]).map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                        activeTab === tab.key
                                            ? "border-gray-900 text-gray-900"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === "linkedin" && <LinkedinFunnelPanel />}

                        {activeTab === "done" && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place récupérés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · google_place done avec data</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#f59e0b")}
                                            series={[{ name: "Google Place récupérés", data: googlePlaceDoneSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place DFS récupérés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · google_place_DFS done avec data</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#eab308")}
                                            series={[{ name: "Google Place DFS récupérés", data: googlePlaceDFSDoneSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Événements avec fondateurs par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events avec au moins un fondateur</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#3b82f6")}
                                            series={[{ name: "Fondateurs ajoutés", data: foundersAddedSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Dates de naissance enrichies par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · personnes avec date de naissance enrichie</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#10b981")}
                                            series={[{ name: "Dates de naissance", data: birthdateCompletedSeries }]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "google_place_dfs" && (
                            <div className="flex flex-col gap-4">
                                {(() => {
                                    const dfs = stats.allTimeDFS;
                                    const total = dfs.pending + dfs.done + dfs.waitingCallback + dfs.approved + dfs.error + dfs.retry;
                                    return (
                                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                                            <DFSKpiCard label="Pending" value={dfs.pending} total={total} valueColor="text-orange-500" />
                                            <DFSKpiCard label="Done" value={dfs.done} total={total} valueColor="text-gray-700" />
                                            <DFSKpiCard label="Waiting callback" value={dfs.waitingCallback} total={total} valueColor="text-purple-600" />
                                            <DFSKpiCard label="Approved" value={dfs.approved} total={total} valueColor="text-green-600" />
                                            <DFSKpiCard label="Error" value={dfs.error} total={total} valueColor="text-red-600" />
                                            <DFSKpiCard label="Retry" value={dfs.retry} total={total} valueColor="text-amber-500" />
                                        </div>
                                    );
                                })()}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place DFS — statuts par semaine de création
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events groupés par semaine de création</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#f97316", "#3b82f6", "#a855f7", "#22c55e", "#ef4444", "#f59e0b"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: labels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "Pending", data: dfsPendingSeries },
                                                { name: "Done", data: dfsDoneSeries },
                                                { name: "Waiting callback", data: dfsWaitingCallbackSeries },
                                                { name: "Approved", data: dfsApprovedSeries },
                                                { name: "Error", data: dfsErrorSeries },
                                                { name: "Retry", data: dfsRetrySeries },
                                            ]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place DFS — statuts par mois de création
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">6 derniers mois · events groupés par mois de création</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#f97316", "#3b82f6", "#a855f7", "#22c55e", "#ef4444", "#f59e0b"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: monthlyLabels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "Pending", data: monthlyDfsPendingSeries },
                                                { name: "Done", data: monthlyDfsDoneSeries },
                                                { name: "Waiting callback", data: monthlyDfsWaitingCallbackSeries },
                                                { name: "Approved", data: monthlyDfsApprovedSeries },
                                                { name: "Error", data: monthlyDfsErrorSeries },
                                                { name: "Retry", data: monthlyDfsRetrySeries },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "email" && (
                            <div className="flex flex-col gap-4">
                                {(() => {
                                    const email = stats.allTimeEmail;
                                    const total = email.done + email.pendingNoWebsite + email.pendingWithWebsite;
                                    return (
                                        <div className="grid grid-cols-3 gap-4">
                                            <DFSKpiCard label="Done" value={email.done} total={total} valueColor="text-green-600" />
                                            <DFSKpiCard label="Pending (sans site web)" value={email.pendingNoWebsite} total={total} valueColor="text-red-500" />
                                            <DFSKpiCard label="Pending (avec site web)" value={email.pendingWithWebsite} total={total} valueColor="text-orange-500" />
                                        </div>
                                    );
                                })()}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Email — par semaine de création
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events groupés par semaine de création</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#22c55e", "#ef4444", "#f97316"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: labels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "Done", data: emailDoneSeries },
                                                { name: "Pending (sans site web)", data: emailPendingNoWebsiteSeries },
                                                { name: "Pending (avec site web)", data: emailPendingWithWebsiteSeries },
                                            ]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Email — par mois de création
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">6 derniers mois · events groupés par mois de création</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#22c55e", "#ef4444", "#f97316"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: monthlyLabels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "Done", data: monthlyEmailDoneSeries },
                                                { name: "Pending (sans site web)", data: monthlyEmailPendingNoWebsiteSeries },
                                                { name: "Pending (avec site web)", data: monthlyEmailPendingWithWebsiteSeries },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "ai_confidence" && (
                            <div className="flex flex-col gap-4">
                                {(() => {
                                    const conf = stats.allTimeConfidence;
                                    const total = conf.high + conf.medium + conf.low + conf.unverified;
                                    return (
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            <DFSKpiCard label="High" value={conf.high} total={total} valueColor="text-green-600" />
                                            <DFSKpiCard label="Medium" value={conf.medium} total={total} valueColor="text-amber-500" />
                                            <DFSKpiCard label="Low" value={conf.low} total={total} valueColor="text-red-500" />
                                            <DFSKpiCard label="Non vérifié" value={conf.unverified} total={total} valueColor="text-gray-400" />
                                        </div>
                                    );
                                })()}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        AI Confidence Level — par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · google_place_DFS done · regroupés par lastRunAt</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#22c55e", "#f59e0b", "#ef4444", "#9ca3af"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: labels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "High", data: confidenceHighSeries },
                                                { name: "Medium", data: confidenceMediumSeries },
                                                { name: "Low", data: confidenceLowSeries },
                                                { name: "Non vérifié", data: confidenceUnverifiedSeries },
                                            ]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        AI Confidence Level — par mois
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">6 derniers mois · google_place_DFS done · regroupés par lastRunAt</p>
                                    <div className="h-72">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={{
                                                chart: {
                                                    type: "bar",
                                                    stacked: true,
                                                    toolbar: { show: false },
                                                    fontFamily: "inherit",
                                                    background: "transparent",
                                                },
                                                plotOptions: {
                                                    bar: { borderRadius: 3, columnWidth: "55%" },
                                                },
                                                colors: ["#22c55e", "#f59e0b", "#ef4444", "#9ca3af"],
                                                dataLabels: { enabled: false },
                                                xaxis: {
                                                    categories: monthlyLabels,
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                    axisBorder: { show: false },
                                                    axisTicks: { show: false },
                                                },
                                                yaxis: {
                                                    labels: { style: { colors: "#6b7280", fontSize: "11px" } },
                                                },
                                                grid: {
                                                    borderColor: "#f3f4f6",
                                                    strokeDashArray: 3,
                                                    xaxis: { lines: { show: false } },
                                                },
                                                tooltip: {
                                                    theme: "light",
                                                    y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
                                                },
                                                legend: {
                                                    show: true,
                                                    position: "top",
                                                    horizontalAlign: "left",
                                                    markers: { width: 8, height: 8 },
                                                    labels: { colors: "#374151" },
                                                    fontSize: "12px",
                                                },
                                            }}
                                            series={[
                                                { name: "High", data: monthlyConfidenceHighSeries },
                                                { name: "Medium", data: monthlyConfidenceMediumSeries },
                                                { name: "Low", data: monthlyConfidenceLowSeries },
                                                { name: "Non vérifié", data: monthlyConfidenceUnverifiedSeries },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "pending" && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place en attente par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#f97316")}
                                            series={[{ name: "En attente", data: googlePlacePendingSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Google Place DFS en attente par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#ef4444")}
                                            series={[{ name: "En attente", data: googlePlaceDFSPendingSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        LinkedIn personnes en attente par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de la personne · sans lastRunAt</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#8b5cf6")}
                                            series={[{ name: "LinkedIn en attente", data: linkedinPendingSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Dates de naissance en attente par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de la personne</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#14b8a6")}
                                            series={[{ name: "En attente", data: birthdatePendingSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Événements sans personnes associées par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#ef4444")}
                                            series={[{ name: "Sans personnes", data: noPeopleSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Événements sans site web par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event · enrichissement website non complété</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#f59e0b")}
                                            series={[{ name: "Sans site web", data: noWebsiteSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Événements sans email par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event · enrichissement company_email non complété</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#6366f1")}
                                            series={[{ name: "Sans email", data: noEmailSeries }]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
